import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Trash2, Mail, Phone, Building2, MapPin, Filter, RefreshCw, Inbox,
  Inbox as InboxIcon, FileEdit, ImageIcon, Sparkles, Layout, ShieldCheck,
  TrendingUp, Briefcase, Package, Heart, Users, ListOrdered, HelpCircle,
  Newspaper, Award, Scale, Palette, Search, KeyRound, Settings2,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/ContentContext";
import { CONTENT_SCHEMA } from "../lib/contentSchema";
import ContentEditor from "../components/admin/ContentEditor";
import ImageLibraryEditor from "../components/admin/ImageLibraryEditor";
import VisualSettingsEditor from "../components/admin/VisualSettingsEditor";
import SeoSettingsEditor from "../components/admin/SeoSettingsEditor";
import SecuritySettingsEditor from "../components/admin/SecuritySettingsEditor";
import { ADMIN_CONTENT_PAGES } from "../lib/adminContentPages";

const STATUSES = ["new", "in_progress", "closed"];
const ADMIN_DASHBOARD_BASE = "/admin/dashboard";
const ADMIN_CONTENT_BASE = `${ADMIN_DASHBOARD_BASE}/content`;
const ADMIN_SETTINGS_BASE = `${ADMIN_DASHBOARD_BASE}/settings`;

const SCHEMA_ICONS = {
  Building2, Sparkles, Layout, ShieldCheck, TrendingUp, Briefcase, Package, Heart, Users,
  ListOrdered, HelpCircle, Newspaper, Award, Scale, Mail,
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/admin" replace />;
  if (location.pathname === ADMIN_DASHBOARD_BASE || location.pathname === `${ADMIN_DASHBOARD_BASE}/`) {
    return <Navigate to={`${ADMIN_DASHBOARD_BASE}/enquiries`} replace />;
  }

  const handleLogout = () => { logout(); navigate("/admin"); };

  return (
    <div className="min-h-[calc(100vh-7rem)] theme-bg-secondary py-10" data-testid="admin-dashboard">
      <div className="container-x">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow mb-2">ADMIN</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold theme-text-primary">Site Management</h1>
            <p className="text-sm theme-text-muted mt-1">Signed in as {user.email}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleLogout} className="btn-secondary" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 theme-surface p-2 rounded-2xl w-fit">
          <NavTab to={`${ADMIN_DASHBOARD_BASE}/enquiries`} icon={InboxIcon} label="Enquiries" testid="tab-enquiries" />
          <NavTab to={ADMIN_CONTENT_BASE} icon={FileEdit} label="Content" testid="tab-content" />
          <NavTab to={`${ADMIN_DASHBOARD_BASE}/images`} icon={ImageIcon} label="Images" testid="tab-images" />
          <NavTab to={`${ADMIN_SETTINGS_BASE}/visual`} icon={Settings2} label="Settings" testid="tab-settings" />
        </div>

        <Routes>
          <Route path="enquiries" element={<EnquiriesPanel />} />
          <Route path="images" element={<ImageLibraryEditor />} />
          <Route path="settings/*" element={<SettingsPanel />} />
          <Route path="content" element={<ContentPagesIndex />} />
          <Route path="content/:pageKey" element={<ContentPage />} />
          <Route path="content/:pageKey/:sectionKey" element={<ContentSection />} />
          <Route path="*" element={<Navigate to={`${ADMIN_DASHBOARD_BASE}/enquiries`} replace />} />
        </Routes>
      </div>
    </div>
  );
}

function SettingsPanel() {
  return (
    <div className="space-y-6" data-testid="admin-settings-panel">
      <div className="flex flex-wrap gap-2 theme-surface p-2 rounded-2xl w-fit">
        <NavTab to={`${ADMIN_SETTINGS_BASE}/visual`} icon={Palette} label="Visual" testid="tab-settings-visual" />
        <NavTab to={`${ADMIN_SETTINGS_BASE}/seo`} icon={Search} label="SEO" testid="tab-settings-seo" />
        <NavTab to={`${ADMIN_SETTINGS_BASE}/security`} icon={KeyRound} label="Security" testid="tab-settings-security" />
      </div>

      <Routes>
        <Route path="" element={<Navigate to={`${ADMIN_SETTINGS_BASE}/visual`} replace />} />
        <Route path="visual" element={<VisualSettingsEditor />} />
        <Route path="seo" element={<SeoSettingsEditor />} />
        <Route path="security" element={<SecuritySettingsEditor />} />
        <Route path="*" element={<Navigate to={`${ADMIN_SETTINGS_BASE}/visual`} replace />} />
      </Routes>
    </div>
  );
}

function NavTab({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
          isActive ? "bg-[var(--interactive-bg)] text-[var(--interactive-text)]" : "theme-text-primary hover:bg-[var(--bg-secondary)]"
        }`
      }
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}

function EnquiriesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [fetching, setFetching] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch {
      toast.error("Failed to load enquiries.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}?status=${status}`);
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl font-bold theme-text-primary">Enquiries</h2>
        <button onClick={load} className="btn-secondary" data-testid="admin-refresh">
          <RefreshCw className={`w-4 h-4 ${fetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total" value={quotes.length} />
        <StatCard label="New" value={quotes.filter((q) => q.status === "new").length} highlight />
        <StatCard label="In Progress" value={quotes.filter((q) => q.status === "in_progress").length} />
        <StatCard label="Closed" value={quotes.filter((q) => q.status === "closed").length} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter className="w-4 h-4 theme-text-muted" />
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold border transition-all ${
              filter === s ? "bg-[var(--interactive-bg)] text-[var(--interactive-text)] border-[var(--interactive-bg)]" : "theme-bg-primary theme-text-primary border-[var(--border-light)] hover:border-[var(--gold)]"
            }`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {filtered.length === 0 ? (
            <div className="theme-surface rounded-2xl p-16 text-center" data-testid="empty-state">
              <Inbox className="w-10 h-10 theme-text-accent mx-auto mb-4 opacity-60" />
              <div className="font-display text-xl font-bold theme-text-primary">No enquiries yet</div>
              <p className="text-sm theme-text-muted mt-2">Submissions will appear here as they arrive.</p>
            </div>
          ) : (
            filtered.map((q) => (
              <button key={q.id} onClick={() => setSelected(q)} data-testid={`quote-row-${q.id}`}
                className={`w-full text-left theme-bg-primary rounded-2xl border p-5 transition-all hover:shadow-md ${
                  selected?.id === q.id ? "border-[var(--gold)] shadow-md" : "border-[var(--border-light)]"
                }`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="font-display text-lg font-bold theme-text-primary">{q.full_name}</div>
                  <StatusPill status={q.status} />
                </div>
                <div className="text-sm theme-text-muted mt-1">
                  {q.company || "—"} · {q.country || "—"} · <span className="theme-text-accent font-semibold">{q.intent} / {q.metal}</span>
                </div>
                <div className="text-xs theme-text-muted mt-3">{fmtDate(q.created_at)}</div>
              </button>
            ))
          )}
        </div>

        <div className="lg:sticky lg:top-32 self-start">
          {selected ? (
            <div className="theme-surface rounded-3xl p-7" data-testid="quote-detail">
              <div className="flex justify-between items-start mb-5">
                <div>
                  <div className="eyebrow mb-2">ENQUIRY DETAIL</div>
                  <h3 className="font-display text-2xl font-bold theme-text-primary">{selected.full_name}</h3>
                  <div className="text-xs theme-text-muted mt-1">{fmtDate(selected.created_at)}</div>
                </div>
                <button onClick={() => remove(selected.id)} className="text-red-500 p-2 hover:bg-red-50 rounded-full" data-testid="quote-delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <Row icon={Mail} label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#B8860B]">{selected.email}</a>} />
                {selected.phone && <Row icon={Phone} label="Phone" value={selected.phone} />}
                {selected.company && <Row icon={Building2} label="Company" value={selected.company} />}
                {selected.country && <Row icon={MapPin} label="Country" value={selected.country} />}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Tag label="Intent" value={selected.intent} />
                <Tag label="Metal" value={selected.metal} />
              </div>

              {selected.product_quantity && (
                <div className="mt-5">
                  <div className="label-x">Product & quantity</div>
                  <div className="text-sm theme-text-primary">{selected.product_quantity}</div>
                </div>
              )}
              {selected.message && (
                <div className="mt-4">
                  <div className="label-x">Message</div>
                  <div className="text-sm theme-text-secondary whitespace-pre-line">{selected.message}</div>
                </div>
              )}

              <div className="mt-7 border-t border-[var(--border-light)] pt-5">
                <div className="label-x mb-3">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => updateStatus(selected.id, s)} data-testid={`set-status-${s}`}
                      className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold border ${
                        selected.status === s ? "bg-[var(--gold)] text-[var(--button-text)] border-[var(--gold)]" : "theme-bg-primary theme-text-primary border-[var(--border-light)] hover:border-[var(--gold)]"
                      }`}>
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="theme-surface rounded-3xl p-10 text-center theme-text-muted text-sm">
              Select an enquiry to view details.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "bg-[var(--interactive-bg)] text-[var(--interactive-text)] border-[var(--interactive-bg)]" : "theme-bg-primary border-[var(--border-light)]"}`}>
      <div className={`text-[10px] tracking-[0.22em] font-semibold ${highlight ? "text-[var(--gold-hover)]" : "theme-text-accent"}`}>{label.toUpperCase()}</div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-[var(--bg-secondary)] text-[var(--gold)] border-[var(--border-gold)]",
    in_progress: "bg-[var(--bg-primary)] text-[var(--text-primary)] border-[var(--border-light)]",
    closed: "bg-[var(--interactive-bg)] text-[var(--interactive-text)] border-[var(--interactive-bg)]",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${map[status] || ""}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 theme-text-accent" />
      <div className="text-xs uppercase tracking-wider theme-text-muted w-20">{label}</div>
      <div className="theme-text-primary flex-1">{value}</div>
    </div>
  );
}

function Tag({ label, value }) {
  return (
    <div className="theme-bg-secondary rounded-xl px-4 py-3">
      <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold">{label.toUpperCase()}</div>
      <div className="text-sm font-semibold theme-text-primary mt-0.5">{value}</div>
    </div>
  );
}

function ContentPagesIndex() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-content-pages">
      {ADMIN_CONTENT_PAGES.map((p) => {
        const Icon = SCHEMA_ICONS[p.icon] || FileEdit;
        return (
          <Link
            key={p.key}
            to={`${ADMIN_CONTENT_BASE}/${p.key}`}
            className="bg-white rounded-3xl border border-[#E5E0D8] p-6 hover:shadow-md transition-shadow"
            data-testid={`content-page-${p.key}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#F5F2EB] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#B8860B]" />
              </div>
              <div>
                <div className="font-display text-xl font-bold text-[#1A1A1A]">{p.label}</div>
                <div className="text-xs text-[#6B6B6B] mt-1">{p.sections.length} sections</div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function ContentPage() {
  const { pageKey } = useParams();
  const page = ADMIN_CONTENT_PAGES.find((p) => p.key === pageKey);
  if (!page) return <Navigate to={ADMIN_CONTENT_BASE} replace />;

  const sections = page.sections
    .map((k) => CONTENT_SCHEMA.find((s) => s.key === k))
    .filter(Boolean);

  return (
    <div className="space-y-5" data-testid={`admin-content-page-${page.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">CONTENT</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A]">{page.label}</h2>
        </div>
        <Link to={ADMIN_CONTENT_BASE} className="btn-secondary" data-testid="back-to-content-index">
          Back to pages
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {sections.map((s) => {
          const Icon = SCHEMA_ICONS[s.icon] || FileEdit;
          return (
            <Link
              key={s.key}
              to={`${ADMIN_CONTENT_BASE}/${page.key}/${s.key}`}
              className="bg-white rounded-3xl border border-[#E5E0D8] p-6 hover:shadow-md transition-shadow"
              data-testid={`content-section-${page.key}-${s.key}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#F5F2EB] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#B8860B]" />
                </div>
                <div>
                  <div className="font-display text-lg font-bold text-[#1A1A1A]">{s.label}</div>
                  {s.description && <div className="text-xs text-[#6B6B6B] mt-1">{s.description}</div>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ContentSection() {
  const { pageKey, sectionKey } = useParams();
  const { refreshSection } = useSiteContent();
  const [loadingSection, setLoadingSection] = useState(true);
  const page = ADMIN_CONTENT_PAGES.find((p) => p.key === pageKey);
  const sectionAllowed = !!page?.sections.includes(sectionKey);
  const schema = sectionAllowed ? CONTENT_SCHEMA.find((s) => s.key === sectionKey) : null;
  const schemaKey = schema?.key;

  useEffect(() => {
    if (!schemaKey) {
      setLoadingSection(false);
      return;
    }
    let mounted = true;
    const run = async () => {
      if (typeof refreshSection !== "function") {
        if (mounted) setLoadingSection(false);
        return;
      }
      try {
        await refreshSection(schemaKey);
      } catch {
      } finally {
        if (mounted) setLoadingSection(false);
      }
    };
    setLoadingSection(true);
    run();
    return () => { mounted = false; };
  }, [refreshSection, schemaKey]);

  if (!page) return <Navigate to={ADMIN_CONTENT_BASE} replace />;
  if (!sectionAllowed) return <Navigate to={`${ADMIN_CONTENT_BASE}/${page.key}`} replace />;
  if (!schema) return <Navigate to={`${ADMIN_CONTENT_BASE}/${page.key}`} replace />;

  return (
    <div className="space-y-5" data-testid={`admin-content-section-${page.key}-${schema.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">{page.label}</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#1A1A1A]">{schema.label}</h2>
        </div>
        <div className="flex gap-2">
          <Link to={`${ADMIN_CONTENT_BASE}/${page.key}`} className="btn-secondary" data-testid="back-to-page">
            Back to {page.label}
          </Link>
        </div>
      </div>

      {loadingSection ? (
        <div className="bg-white rounded-3xl border border-[#E5E0D8] p-10 text-sm text-[#6B6B6B]">
          Loading…
        </div>
      ) : (
        <ContentEditor schema={schema} />
      )}
    </div>
  );
}
